<template>
    <div class="company-contact-form">
        <CompassFormItem
            :label="$t.__('CompanyInformation.contact_information.company_name_label')"
            :info="$t.__('CompanyInformation.contact_information.company_name_help')"
            orientation="horizontal"
            :required="true"
            :error="errors.companyName"
        >
            <CompassInput
                v-model="companyName"
                :error="!!errors.companyName"
                @focus="errors.companyName = ''"
                @blur="checkCompanyName"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.contact_information.email_label')"
            orientation="horizontal"
            :required="true"
            :error="errors.email"
        >
            <CompassInput v-model="email" type="email" :error="!!errors.email" @blur="checkEmail" />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.contact_information.phone_label')"
            orientation="horizontal"
            :error="errors.phone ? $t.__('common.labels.please_enter_a_valid_phone_number') : ''"
        >
            <CompassTelInput
                :value="phone"
                :defaultCountry="country_code"
                :error="errors.phone"
                @update:modelValue="errors.phone = false"
                @blur="($event) => handleTelInputBlur($event)"
                @validate="(value) => updatePhoneNumber(value)"
                @country-changed="handleCountryChange($event)"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.contact_information.website_label')"
            orientation="horizontal"
            :error="errors.website"
        >
            <CompassInput v-model="website" :error="!!errors.website" @blur="checkWebsite" />
        </CompassFormItem>

        <CompassButton
            class="company-contact-form__save-button"
            type="tertiary"
            :label="$t.__('CompanyInformation.contact_information.save_button')"
            :disabled="disableSubmit"
            :loading="isLoading"
            @click="handleSubmit"
        />
    </div>
</template>
<script>
import { validatePhoneNumber } from 'Helpers/validator/PhoneNumberValidator';
import { validateUrl } from 'Helpers/validator/UrlValidator';
import { updateAccountInformation } from 'api/account/account';
import { validateEmail } from 'Helpers/validator/EmailValidator';

export default {
    props: {
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            country_code: this.data.country_code || 'FR',
            companyName: this.data.name || '',
            email: this.data.email || '',
            phone: this.data.phone_number || '',
            website: this.data.website || '',
            country: {},
            errors: {
                companyName: '',
                email: '',
                phone: '',
                website: '',
            },
            isLoading: false,
        };
    },
    computed: {
        disableSubmit() {
            return Object.values(this.errors).some(Boolean);
        },
    },
    methods: {
        validatePhoneNumber,
        validateUrl,
        handleCountryChange($event) {
            this.country = $event;
        },
        updatePhoneNumber(value) {
            this.phone = value.valid ? value.formatted : '';
        },
        handleTelInputBlur($event) {
            if (!$event || $event.target.closest('.compass-select-flag')) {
                this.errors.phone = false;
                return;
            }

            const inputValue = $event.target.value;
            if (!inputValue || inputValue.replace('+', '') === this.country.dialCode) {
                this.errors.phone = false;
                return;
            }

            this.errors.phone = !this.validatePhoneNumber(this.phone, this.country.iso2);
        },
        checkCompanyName() {
            if (!this.companyName) {
                this.errors.companyName = this.$t.__('common.labels.this_field_is_required');
                return;
            }

            if (this.companyName.length > 250) {
                this.errors.companyName = this.$t.__(
                    'CompanyInformation.contact_information.company_name.errors.length',
                );
                return;
            }

            if (this.validateUrl(this.companyName)) {
                this.errors.companyName = this.$t.__(
                    'CompanyInformation.contact_information.company_name.errors.url',
                );
                return;
            }
            this.errors.companyName = '';
        },
        checkEmail() {
            if (!this.email) {
                this.errors.email = this.$t.__('common.labels.this_field_is_required');
                return;
            }

            if (!validateEmail(this.email)) {
                this.errors.email = this.$t.__('common.labels.please_enter_a_valid_email_address');
                return;
            }
            this.errors.email = '';
        },
        checkWebsite() {
            if (this.website && !this.validateUrl(this.website)) {
                this.errors.website = this.$t.__('common.labels.please_enter_a_valid_url');
                return;
            }
            this.errors.website = '';
        },
        handleSubmit() {
            this.isLoading = true;
            const payload = {
                name: this.companyName,
                email: this.email,
                phone_number: this.phone,
                website: this.website,
            };
            updateAccountInformation(payload, ['name', 'website', 'email', 'phone_number'])
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        text: this.$t.__('CompanyInformation.contact_information.save.success'),
                    });
                })
                .catch((error) => {
                    const apiError = error.response?.data?.error.details;
                    const key = apiError ? Object.keys(apiError)[0] : null;
                    const value = apiError ? Object.values(apiError)[0] : null;

                    if (key && value) {
                        if (key === 'name') {
                            this.errors.companyName = value;
                        } else {
                            this.errors[key] = value;
                        }
                    }

                    this.$pushToast({
                        type: 'error',
                        text: this.$t.__('CompanyInformation.contact_information.save.error'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>
<style lang="scss" scoped>
.company-contact-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    &::v-deep {
        .compass-tel-input .compass-input {
            flex: 1;
        }
    }

    &__save-button {
        display: block;
        margin-left: auto;
    }
}
</style>
